import { useLayoutEffect, useRef } from "react";

/**
 * Pin a table header to the top of `.app-main-container` while the page scrolls.
 * Needed when the table wrap uses overflow-x (width inside frame): CSS sticky top
 * cannot track the page scroller in current browsers.
 *
 * @param {unknown[]} [deps=[]] Re-bind when table content/columns change.
 * @returns {React.MutableRefObject<HTMLElement|null>} Attach to the table wrap element.
 */
export function usePageScrollPinnedTableHeader(deps = []) {
  const tableWrapRef = useRef(null);

  useLayoutEffect(() => {
    const wrap = tableWrapRef.current;
    const main = document.querySelector(".app-main-container");
    if (!wrap || !main) return undefined;

    const syncPinnedHeader = () => {
      const thead = wrap.querySelector("thead");
      if (!thead) return;
      const thCells = thead.querySelectorAll("th");
      const mainRect = main.getBoundingClientRect();
      const wrapRect = wrap.getBoundingClientRect();
      const theadHeight = thead.offsetHeight || 0;
      const shouldPin =
        wrapRect.top < mainRect.top &&
        wrapRect.bottom - theadHeight > mainRect.top;
      const y = shouldPin ? Math.round(mainRect.top - wrapRect.top) : 0;

      wrap.classList.toggle("is-header-pinned", y > 0);
      thCells.forEach((th) => {
        th.style.transform = y > 0 ? `translateY(${y}px)` : "";
      });
    };

    syncPinnedHeader();
    main.addEventListener("scroll", syncPinnedHeader, { passive: true });
    wrap.addEventListener("scroll", syncPinnedHeader, { passive: true });
    window.addEventListener("resize", syncPinnedHeader);
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(syncPinnedHeader) : null;
    ro?.observe(wrap);

    return () => {
      main.removeEventListener("scroll", syncPinnedHeader);
      wrap.removeEventListener("scroll", syncPinnedHeader);
      window.removeEventListener("resize", syncPinnedHeader);
      ro?.disconnect();
      wrap.classList.remove("is-header-pinned");
      wrap.querySelectorAll("thead th").forEach((th) => {
        th.style.transform = "";
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- caller passes content deps
  }, deps);

  return tableWrapRef;
}
